import * as R from 'ramda';

import { ComplianceTypes, SessionTypes } from '@/constants';
import {
  ChapterState,
  ComplianceType,
  EpisodeStatus,
  SessionChapter,
  SessionSummary,
  UserChapterProgress,
} from '@/types';
import { camelCaseKeys, convertHHMMSStoSeconds } from '@/utils';

import {
  PlayerSessionResponse,
  PlayerSessionTransitionResponse,
} from '../player-fsm/player-fsm.types';

export const transformSessionChapter = (
  chapter: SessionChapter,
): UserChapterProgress => ({
  chapterId: chapter.chapterId,
  id: chapter.chapterId,
  state: chapter.state,
  lastPlayed: chapter.lastEvent,
  completed: ['quiz_ready', 'verified', 'completed'].some(
    s => s === chapter.state,
  ),
  progress: {
    seekableDuration: chapter.maxPosition ?? 0,
    playableDuration: chapter.duration,
    currentTime: chapter?.position ?? 0,
    playedDuration: chapter.maxPosition ?? 0,
  },
  isVerifiable: chapter.class.includes('verifiable'),
  isTrailer: chapter.class.includes('trailer'),
  quizId: chapter.quizId,
  quizIds: chapter.quizIds,
  completedQuizIds: chapter.completedQuizIds,
});

export const transformSessionChapters = (
  chapters: SessionChapter[],
): UserChapterProgress[] => {
  return R.map(transformSessionChapter, chapters);
};

export const transformSessionResponse = (
  sessionResponse: any,
): PlayerSessionResponse => {
  return R.pipe(camelCaseKeys, response => ({
    ...response,
    state: {
      ...response.state,
      chapters: transformSessionChapters(response.state.chapters),
    },
  }))(sessionResponse) as PlayerSessionResponse;
};

export const transformSessionsResponse = (
  sessionsResponse: any[],
): SessionSummary[] => {
  return camelCaseKeys(sessionsResponse).map((session: any) => ({
    ...session,
    availableQuizCount:
      session.chapterStates?.filter(
        (state: ChapterState) => state === 'quiz_ready',
      )?.length ?? 0,
    playedDuration: convertHHMMSStoSeconds(session.playedDuration),
    status: calculateEpisodeStatus(session),
    isRedeemed: !!session.extraStates?.claimed,
  }));
};

type EpisodeSessionPayload = Pick<
  SessionSummary,
  'chapters' | 'extraStates' | 'fsmType'
>;

export const calculateEpisodeStatus = (
  e: EpisodeSessionPayload,
): EpisodeStatus => {
  switch (e.fsmType) {
    case SessionTypes.CPE:
      return getFirstMatchingStatus(e, [
        checkForGenericStatus,
        calculateCPEEpisodeStatus,
        checkForEmptyStatus,
      ]);
    case SessionTypes.CPD:
      return getFirstMatchingStatus(e, [
        checkForGenericStatus,
        calculateCPDEpisodeStatus,
        checkForEmptyStatus,
      ]);
    default:
      return getFirstMatchingStatus(e, [
        checkForGenericStatus,
        checkForEmptyStatus,
      ]);
  }
};

// Loops through a set of status checks and returns the first match, or ON_GOING if no checks match
const getFirstMatchingStatus = (
  e: EpisodeSessionPayload,
  checks: Array<(e: EpisodeSessionPayload) => EpisodeStatus | null>,
) => R.find(fn => !!fn(e), checks)?.(e) ?? 'onGoing';

const checkForGenericStatus = (e: EpisodeSessionPayload) => {
  const verifiableChapters = getVerifiableChapters(e);
  const nonVerifiableChapters = getNonVerifiableChapters(e);

  if (
    !verifiableChapters.length &&
    nonVerifiableChapters.every(c => c.state === 'completed')
  )
    return 'completed';

  if (
    !verifiableChapters.length &&
    nonVerifiableChapters.some(c => c.state === 'ready')
  )
    return 'none';

  return null;
};

const checkForEmptyStatus = (e: EpisodeSessionPayload) => {
  const verifiableChapters = getVerifiableChapters(e);
  if (
    verifiableChapters.every(c => c.state === 'ready') &&
    !e.extraStates?.claimed
  )
    return 'none';

  return null;
};

const calculateCPDEpisodeStatus = (e: EpisodeSessionPayload) => {
  const verifiableChapters = getVerifiableChapters(e);

  if (verifiableChapters.every(c => c.state === 'verified')) return 'verified';

  if (
    verifiableChapters.every(
      c => c.state === 'verified' || c.state === 'quiz_ready',
    )
  )
    return 'quizReady';

  return null;
};

const calculateCPEEpisodeStatus = (e: EpisodeSessionPayload) => {
  if (e.extraStates?.creditState === 'earned') return 'verified';

  const verifiableChapters = getVerifiableChapters(e);
  if (
    verifiableChapters.every(
      c => c.state === 'completed' || c.state === 'quiz_ready',
    )
  )
    return 'assessmentReady';

  return null;
};

const getVerifiableChapters = (e: EpisodeSessionPayload) =>
  e.chapters.filter(c => c.class.includes('verifiable'));

const getNonVerifiableChapters = (e: EpisodeSessionPayload) =>
  e.chapters.filter(c => !c.class.includes('verifiable'));

export const transformSessionResponseNullable = (
  sessionResponse: any,
): PlayerSessionResponse | null => {
  if (sessionResponse === null) {
    return null;
  }
  return transformSessionResponse(sessionResponse);
};

export const transformPlayerTransitionResponse = <
  T extends PlayerSessionTransitionResponse = PlayerSessionTransitionResponse,
>(
  playSessionResponse: any,
): T => {
  return R.pipe(camelCaseKeys, response => ({
    ...response,
    state: {
      ...response.state,
      newState: {
        ...(response.state?.newState ?? {}),
        chapters: transformSessionChapters(
          response.state?.newState?.chapters ?? [],
        ),
      },
    },
  }))(playSessionResponse) as T;
};

export const pluckIdsOfStatus = (status: EpisodeStatus) =>
  R.pipe(
    R.filter((e: SessionSummary) => e.status === status),
    R.values,
    sessions => R.pluck('episodeId')(sessions),
  );

export const getSessionComplianceType = (
  session: SessionSummary,
): ComplianceType => {
  return session.fsmType === SessionTypes.CPE
    ? ComplianceTypes.CPE
    : ComplianceTypes.CPD;
};
